// src/ConsumerApp/DesignSystem/Navigation/Navbar.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../../../assets/theme/theme';
import LogoBB from '../../../assets/icons/logoBB.svg';
import bucket from '../../../assets/icons/bucketConcrete.svg';
import UserCircle from '../../../assets/icons/UserCircle.svg';
import MagnifyingGlass from '../../../assets/icons/MagnifyingGlass.svg';
import CenteredContainer from '../Layout/CenteredContainer';
import DeliveryAddressModal from '../../Screens/Addresses/DeliveryAddressModal';
import Bucket from '../../Screens/Bucket/Bucket';

import { useGetBucketSummary } from '../../api/products/product.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';
import { useAuthModal } from '../../context/AuthModalContext';
import { guestBucketManager } from '../../utils/guestBucketManager';

const SEARCH_PLACEHOLDERS = [
  'Search "Ultratech Cement"',
  'Search "Finolex PVC Pipes"',
  'Search "Asian Paints Primer"',
  'Search "Polycab Wires"',
  'Search "TMT Steel Bars"',
  'Search "Dr. Fixit Waterproofing"',
];

/* const QUICK_CATEGORIES = [
  { id: 'all', label: 'All', icon: '⚡' },
  { id: 'cement', label: 'Cement', icon: '🧱' },
  { id: 'plumbing', label: 'Plumbing', icon: '🚰' },
  { id: 'electrical', label: 'Electrical', icon: '💡' },
  { id: 'paints', label: 'Paints', icon: '🎨' },
  { id: 'steel', label: 'Steel', icon: '🏗️' },
]; */

export default function Navbar({ searchQuery = '', onSearchChange, onVoiceSearchClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openLoginModal } = useAuthModal();

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isBucketOpen, setIsBucketOpen] = useState(false);

  // 1. Reactive customer authentication state
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // 2. Reactive guest cart count
  const [guestCount, setGuestCount] = useState(() => guestBucketManager.getTotalCount());

  // 3. Cycling placeholder animation
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % SEARCH_PLACEHOLDERS.length);
    }, 2800);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        const parsed = stored ? JSON.parse(stored) : null;
        setCustomer(parsed);
        if (parsed?._id || parsed?.id) {
          guestBucketManager.clearGuestBucket();
        }
      } catch {
        setCustomer(null);
      }
    };

    const handleGuestBucketChange = () => {
      setGuestCount(guestBucketManager.getTotalCount());
    };

    window.addEventListener('auth-state-change', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('guest-bucket-change', handleGuestBucketChange);

    return () => {
      window.removeEventListener('auth-state-change', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('guest-bucket-change', handleGuestBucketChange);
    };
  }, []);

  const customerId = customer?._id || customer?.id;

  // Server queries
  const { data: addressRes } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary } = useGetBucketSummary(customerId);

  const cartItemCount = customerId
    ? bucketSummary?.buy_now_bucket?.items_count || 0
    : guestCount;

  // Address Display Formatting
  const { projectName, addressLocality, fullTooltip } = useMemo(() => {
    const addr = addressRes?.data?.address || addressRes?.data || addressRes?.address || addressRes;
    if (!addr) {
      return {
        projectName: '',
        addressLocality: customerId ? 'Select site delivery address' : 'Select delivery location',
        fullTooltip: 'Select delivery address',
      };
    }

    const pName = addr.project_name ? addr.project_name.trim() : '';
    const addressParts = [
      addr.area_sector_locality || addr.flat_number || addr.building_name,
      addr.city,
    ].filter(Boolean);

    const locality = addressParts.length > 0
      ? addressParts.join(', ')
      : (addr.complete_address || 'Current Site');

    return {
      projectName: pName,
      addressLocality: locality,
      fullTooltip: pName ? `${pName} - ${locality}` : locality,
    };
  }, [addressRes, customerId]);

  const handleSearchInteraction = () => {
    if (location.pathname !== '/consumer/search') {
      const targetQuery = searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : '';
      navigate(`/consumer/search${targetQuery}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const queryParam = e.target.value.trim();
      navigate(`/consumer/search?q=${encodeURIComponent(queryParam)}`);
    }
  };

  return (
    <>
      <style>{`
        /* ══════════════════════════════════════════════════════════
           BASE SHELL
           ══════════════════════════════════════════════════════════ */
        .bb-navbar-shell {
          background-color: #FFFFFF;
          border-bottom: 1px solid #E2E8F0;
          position: sticky;
          top: 0;
          z-index: 1000;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          width: 100%;
        }

        /* ══════════════════════════════════════════════════════════
           DESKTOP VIEWPORT (>= 769px) - FULL WIDTH WITH MARGINS
           ══════════════════════════════════════════════════════════ */
        .bb-nav-desktop-layout {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          height: 76px;
        }

        .bb-nav-left-cluster {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
        }

        .bb-nav-brand-logo {
          height: 50px;
          width: auto;
          cursor: pointer;
          display: block;
        }

        .bb-nav-divider {
          width: 1px;
          height: 38px;
          background-color: #E2E8F0;
        }

        .bb-nav-delivery-box {
          display: flex;
          flex-direction: column;
          cursor: pointer;
          max-width: 250px;
        }

        .bb-nav-eta-badge {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .bb-nav-eta-text {
          font-family: ${theme.typography.headingMD.fontFamily};
          font-size: 13.5px;
          font-weight: 800;
          color: #2B2A29;
          letter-spacing: -0.2px;
        }

        .bb-nav-address-line {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-top: 1px;
          overflow: hidden;
          white-space: nowrap;
        }

        .bb-nav-project-text {
          font-size: 12px;
          font-weight: 700;
          color: #2B2A29;
          max-width: 110px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .bb-nav-locality-text {
          font-size: 11.5px;
          color: #64748B;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 130px;
        }

        .bb-nav-search-bar {
          flex: 1;
          max-width: 580px;
          min-width: 280px;
          display: flex;
          align-items: center;
          background-color: #F8FAFC;
          border: 1.5px solid #E2E8F0;
          border-radius: 12px;
          padding: 10px 16px;
          gap: 10px;
          cursor: text;
          box-sizing: border-box;
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }

        .bb-nav-search-bar:focus-within {
          border-color: #CBD5E1;
          background-color: #FFFFFF;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .bb-nav-search-input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-size: 13.5px;
          font-weight: 500;
          color: #2B2A29;
          padding: 0;
        }

        .bb-nav-right-cluster {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-shrink: 0;
        }

        .bb-nav-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 8px;
          outline: none;
          user-select: none;
        }

        .bb-nav-btn-icon-wrap {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
        }

        .bb-nav-btn-label {
          font-size: 11px;
          font-weight: 700;
          color: #2B2A29;
          margin-top: 2px;
        }

        .bb-nav-cart-badge {
          position: absolute;
          top: -4px;
          right: -7px;
          background-color: #E9001E;
          color: #FFFFFF;
          font-size: 10px;
          font-weight: 800;
          border-radius: 999px;
          min-width: 17px;
          height: 17px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
          box-shadow: 0 2px 4px rgba(233, 0, 30, 0.35);
        }

        .bb-nav-mobile-layout {
          display: none;
        }

        .bb-mobile-floating-cart {
          display: none;
        }

        /* ══════════════════════════════════════════════════════════
           TIER-1 QUICK COMMERCE MOBILE NAVBAR (<= 768px)
           Calibrated edge spacing for maximum space utilization
           ══════════════════════════════════════════════════════════ */
        @media (max-width: 768px) {
          .bb-nav-desktop-layout {
            display: none !important;
          }

          /* Overwrite CenteredContainer padding inside the mobile header */
          .bb-navbar-shell > div {
            padding-left: 0 !important;
            padding-right: 0 !important;
            max-width: 100% !important;
          }

          .bb-navbar-shell {
            background-color: #FFFFFF;
            border-bottom: 1px solid #F1F5F9;
            padding: 8px 12px 10px 12px; /* Tight edge padding */
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
          }

          .bb-nav-mobile-layout {
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
          }

          /* ── Row 1: Header / Location + Profile ── */
          .bb-mob-top-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          }

          .bb-mob-location-target {
            display: flex;
            flex-direction: column;
            cursor: pointer;
            flex: 1;
            min-width: 0;
            padding-right: 8px;
          }

          .bb-mob-eta-heading {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .bb-mob-flash-icon {
            font-size: 15px;
            color: #D97706;
            line-height: 1;
          }

          .bb-mob-eta-title {
            font-size: 16px;
            font-weight: 900;
            color: #0F172A;
            letter-spacing: -0.3px;
            line-height: 19px;
          }

          .bb-mob-address-line {
            display: flex;
            align-items: center;
            gap: 4px;
            margin-top: 1px;
            overflow: hidden;
            white-space: nowrap;
          }

          .bb-mob-site-tag {
            font-size: 12px;
            font-weight: 700;
            color: #1E293B;
            max-width: 130px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .bb-mob-site-sub {
            font-size: 12px;
            font-weight: 500;
            color: #475569;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
          }

          .bb-mob-caret-down {
            width: 12px;
            height: 12px;
            flex-shrink: 0;
            color: #1E293B;
          }

          .bb-mob-profile-btn {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background-color: #F8FAFC;
            border: 1px solid #E2E8F0;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            transition: transform 0.15s ease;
          }

          .bb-mob-profile-btn:active {
            transform: scale(0.95);
          }

          /* ── Row 2: Search Input Bar (Expands fully across screen) ── */
          .bb-mob-search-container {
            width: 100%;
            display: flex;
            align-items: center;
            background-color: #F8FAFC;
            border: 1.5px solid #E2E8F0;
            border-radius: 12px;
            padding: 8px 12px;
            gap: 8px;
            box-sizing: border-box;
            box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.02);
            height: 42px;
            cursor: text;
          }

          .bb-mob-search-container:focus-within {
            background-color: #FFFFFF;
            border-color: #CBD5E1;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
          }

          .bb-mob-search-lens {
            width: 16px;
            height: 16px;
            color: #64748B;
            flex-shrink: 0;
          }

          .bb-mob-search-input {
            flex: 1;
            border: none;
            outline: none;
            font-size: 13px;
            font-weight: 600;
            color: #0F172A;
            background: transparent;
            padding: 0;
            line-height: 18px;
          }

          .bb-mob-search-input::placeholder {
            color: #64748B;
            font-weight: 500;
          }

          .bb-mob-mic-btn {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 14px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0.8;
          }

          /* ── Row 3: Quick Category Pills Rail (Edge-to-edge bleed) ── */
          .bb-mob-quick-categories {
            display: flex;
            overflow-x: auto;
            gap: 8px;
            margin: 0 -12px; /* Pulls pills to full screen edge */
            padding: 2px 12px; /* Restores padding inside the scroll track */
            scrollbar-width: none;
            -webkit-overflow-scrolling: touch;
          }

          .bb-mob-quick-categories::-webkit-scrollbar {
            display: none;
          }

          .bb-mob-cat-pill {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px 10px;
            background-color: #F1F5F9;
            border: 1px solid #E2E8F0;
            border-radius: 999px;
            font-size: 11.5px;
            font-weight: 700;
            color: #334155;
            white-space: nowrap;
            flex-shrink: 0;
            cursor: pointer;
            user-select: none;
            transition: all 0.15s ease;
          }

          .bb-mob-cat-pill:active {
            background-color: #E2E8F0;
          }

          /* ── Floating Mobile Cart CTA ── */
          .bb-mobile-floating-cart {
            display: flex;
            position: fixed;
            bottom: 14px;
            left: 12px;
            right: 12px;
            background: linear-gradient(135deg, #E9001E 0%, #C7061F 100%);
            color: #FFFFFF;
            border-radius: 14px;
            padding: 12px 16px;
            align-items: center;
            justify-content: space-between;
            box-shadow: 0 10px 24px rgba(233, 0, 30, 0.42);
            z-index: 9999;
            cursor: pointer;
            box-sizing: border-box;
            animation: slideUpCart 0.22s cubic-bezier(0.16, 1, 0.3, 1);
          }

          @keyframes slideUpCart {
            from {
              opacity: 0;
              transform: translateY(24px) scale(0.97);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .bb-mob-cart-left {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .bb-mob-cart-badge-circle {
            background-color: #FFFFFF;
            color: #E9001E;
            font-size: 12px;
            font-weight: 900;
            width: 24px;
            height: 24px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .bb-mob-cart-title {
            font-size: 13.5px;
            font-weight: 800;
            color: #FFFFFF;
            letter-spacing: 0.1px;
          }

          .bb-mob-cart-right {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 13px;
            font-weight: 700;
            color: #FFFFFF;
          }
        }

        /* ── Compact Mobiles (<= 380px) ── */
        @media (max-width: 380px) {
          .bb-navbar-shell {
            padding: 6px 10px 8px 10px;
          }
          .bb-mob-quick-categories {
            margin: 0 -10px;
            padding: 2px 10px;
          }
          .bb-mobile-floating-cart {
            left: 10px;
            right: 10px;
          }
        }
      `}</style>

      <header className="bb-navbar-shell">
        <CenteredContainer>
          {/* ════════════════════════════════════════════════
              1. DESKTOP VIEWPORT LAYOUT (>= 769px) 
              ════════════════════════════════════════════════ */}
          <div className="bb-nav-desktop-layout">
            <div className="bb-nav-left-cluster">
              <img
                src={LogoBB}
                alt="Brickbucket"
                className="bb-nav-brand-logo"
                onClick={() => navigate('/consumer/HomeScreen')}
              />

              <div className="bb-nav-divider" />

              <div
                className="bb-nav-delivery-box"
                onClick={() => {
                  if (customerId) setIsAddressModalOpen(true);
                  else openLoginModal();
                }}
              >
                <div className="bb-nav-eta-badge">
                  <span style={{ fontSize: 13 }}>⚡</span>
                  <span className="bb-nav-eta-text">Delivery in 30-90 mins</span>
                </div>

                <div className="bb-nav-address-line" title={fullTooltip}>
                  {projectName && (
                    <>
                      <span className="bb-nav-project-text">{projectName}</span>
                      <span style={{ color: '#94A3B8' }}>-</span>
                    </>
                  )}
                  <span className="bb-nav-locality-text">{addressLocality}</span>
                  <span style={{ fontSize: 9, color: '#64748B', marginLeft: 2 }}>▼</span>
                </div>
              </div>
            </div>

            {/* Center Desktop Search Field */}
            <div className="bb-nav-search-bar" onClick={handleSearchInteraction}>
              <img src={MagnifyingGlass} alt="search" style={{ width: 16, height: 16, opacity: 0.55 }} />
              <input
                type="text"
                placeholder={SEARCH_PLACEHOLDERS[placeholderIndex]}
                value={searchQuery}
                onClick={handleSearchInteraction}
                onFocus={handleSearchInteraction}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  if (location.pathname !== '/consumer/search') {
                    navigate(`/consumer/search?q=${encodeURIComponent(e.target.value)}`);
                  } else if (onSearchChange) {
                    onSearchChange(e.target.value);
                  }
                }}
                className="bb-nav-search-input"
              />
              {onVoiceSearchClick && (
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onVoiceSearchClick();
                  }}
                >
                  🎤
                </button>
              )}
            </div>

            {/* Right Desktop Action Cluster */}
            <div className="bb-nav-right-cluster">
              {customerId ? (
                <button
                  type="button"
                  className="bb-nav-btn"
                  onClick={() => navigate('/consumer/account/profile')}
                  title="My Profile"
                >
                  <div className="bb-nav-btn-icon-wrap">
                    <img src={UserCircle} alt="Profile" style={{ width: 26, height: 26 }} />
                  </div>
                  <span className="bb-nav-btn-label">Profile</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="bb-nav-btn"
                  onClick={openLoginModal}
                  title="Login to Account"
                >
                  <div className="bb-nav-btn-icon-wrap">
                    <img src={UserCircle} alt="Login" style={{ width: 26, height: 26 }} />
                  </div>
                  <span className="bb-nav-btn-label">Login</span>
                </button>
              )}

              <button
                type="button"
                className="bb-nav-btn"
                onClick={() => setIsBucketOpen(true)}
                title="View Bucket"
              >
                <div className="bb-nav-btn-icon-wrap">
                  <img src={bucket} alt="Bucket" style={{ width: 26, height: 26 }} />
                  {cartItemCount > 0 && <span className="bb-nav-cart-badge">{cartItemCount}</span>}
                </div>
                <span className="bb-nav-btn-label">Bucket</span>
              </button>
            </div>
          </div>

          {/* ════════════════════════════════════════════════
              2. PRODUCTION QUICK-COMMERCE MOBILE LAYOUT (<= 768px)
              ════════════════════════════════════════════════ */}
          <div className="bb-nav-mobile-layout">
            {/* Row 1: High-Contrast Delivery Promise & Address + Profile Pill */}
            <div className="bb-mob-top-bar">
              <div
                className="bb-mob-location-target"
                onClick={() => {
                  if (customerId) setIsAddressModalOpen(true);
                  else openLoginModal();
                }}
              >
                <div className="bb-mob-eta-heading">
                  <span className="bb-mob-flash-icon">⚡</span>
                  <span className="bb-mob-eta-title">Delivery in 30–90 mins</span>
                </div>

                <div className="bb-mob-address-line">
                  {projectName && <span className="bb-mob-site-tag">{projectName} -</span>}
                  <span className="bb-mob-site-sub" title={fullTooltip}>
                    {addressLocality}
                  </span>
                  <svg className="bb-mob-caret-down" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              {/* Profile Avatar Pill */}
              <button
                type="button"
                className="bb-mob-profile-btn"
                onClick={() => {
                  if (customerId) navigate('/consumer/account/profile');
                  else openLoginModal();
                }}
                aria-label="Account profile"
              >
                <img src={UserCircle} alt="Profile" style={{ width: 22, height: 22 }} />
              </button>
            </div>

            {/* Row 2: Focused Search Input Box with Full Screen Breadth */}
            <div className="bb-mob-search-container" onClick={handleSearchInteraction}>
              <svg className="bb-mob-search-lens" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>

              <input
                type="text"
                placeholder={SEARCH_PLACEHOLDERS[placeholderIndex]}
                value={searchQuery}
                onClick={handleSearchInteraction}
                onFocus={handleSearchInteraction}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  if (location.pathname !== '/consumer/search') {
                    navigate(`/consumer/search?q=${encodeURIComponent(e.target.value)}`);
                  } else if (onSearchChange) {
                    onSearchChange(e.target.value);
                  }
                }}
                className="bb-mob-search-input"
              />

              {onVoiceSearchClick && (
                <button
                  type="button"
                  className="bb-mob-mic-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onVoiceSearchClick();
                  }}
                  title="Voice Search"
                >
                  🎤
                </button>
              )}
            </div>

            {/* Row 3: Quick Category Pills Rail */}
           {/*  <div className="bb-mob-quick-categories">
              {QUICK_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className="bb-mob-cat-pill"
                  onClick={() => {
                    if (cat.id === 'all') navigate('/consumer/productslist');
                    else navigate(`/consumer/search?q=${encodeURIComponent(cat.label)}`);
                  }}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div> */}
          </div>
        </CenteredContainer>
      </header>

      {/* Floating Bottom Cart Bar for Mobile */}
      {cartItemCount > 0 && (
        <div
          className="bb-mobile-floating-cart"
          onClick={() => setIsBucketOpen(true)}
        >
          <div className="bb-mob-cart-left">
            <div className="bb-mob-cart-badge-circle">{cartItemCount}</div>
            <span className="bb-mob-cart-title">
              {cartItemCount === 1 ? '1 Item in Bucket' : `${cartItemCount} Items in Bucket`}
            </span>
          </div>

          <div className="bb-mob-cart-right">
            <span>View Bucket</span>
            <span>→</span>
          </div>
        </div>
      )}

      {/* Modals & Slide-over Drawers */}
      {customerId && (
        <DeliveryAddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          onAddNewAddress={(loc) => navigate('/consumer/account/delivery/new', { state: loc })}
        />
      )}

      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />
    </>
  );
}